import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  Diagram,
  DiagramConnection,
  DiagramConnectionAnchor,
  DiagramConnectionId,
  DiagramId,
  DiagramNode,
  DiagramNodeId,
  DiagramRegionId,
} from "platformed-client/types";
import {typedIncludes, unreachableCase} from "../../../../../../../utils/typescript";
import {SpatialIndex} from "./spatialIndex";
import {Vec2} from "./math/vec2";
import {useBackendSync} from "./sync";
import {COLOR_PALETTES, ColorPalette} from "./colors";
import {fixupToolState, ToolState} from "./tools";

export const CELL_SIZE = 64;

type ObjectInfo = {label: string};

export const OBJECT_INFOS = {
  Node: {label: "node"},
  Connection: {label: "connection"},
  ConnectionAnchor: {label: "anchor point"},
} as const satisfies Record<DiagramAnyObject["type"], ObjectInfo>;

export type DiagramAnyObjectId =
  | {type: "Node"; id: DiagramNodeId}
  | {type: "Connection"; id: DiagramConnectionId}
  // A connection anchor is uniquely identified by its connection ID and index.
  | {type: "ConnectionAnchor"; id: DiagramConnectionId; index: number}
  | {type: "Region"; id: DiagramRegionId};

export function serializeAnyObjectId(id: DiagramAnyObjectId) {
  switch (id.type) {
    case "Node":
      return `node:${id.id}`;
    case "Connection":
      return `connection:${id.id}`;
    case "ConnectionAnchor":
      return `connection:${id.id}:${id.index}`;
    case "Region":
      return `region:${id.id}`;
    default:
      unreachableCase(id);
  }
}

export type DiagramAnyObject =
  | {type: "Node"; node: ClientDiagramNode}
  | {type: "Connection"; connection: ClientDiagramConnection}
  | {
      type: "ConnectionAnchor";
      connection: ClientDiagramConnection;
      index: number;
      anchor: ClientDiagramConnectionAnchor;
    };

export function getAnyObjectId(object: DiagramAnyObject): DiagramAnyObjectId {
  switch (object.type) {
    case "Node":
      return {type: "Node", id: object.node.id};
    case "Connection":
      return {type: "Connection", id: object.connection.id};
    case "ConnectionAnchor":
      return {type: "ConnectionAnchor", id: object.connection.id, index: object.index};
    default:
      unreachableCase(object);
  }
}

export function createClientDiagram(diagram: Diagram): ClientDiagram {
  const nodes = new Map(
    Object.values(diagram.nodes)
      .map(convertNode)
      .map(n => [n.id, n]),
  );

  const connections = new Map(
    Object.values(diagram.connections)
      .map(convertConnection)
      .map(c => [c.id, c]),
  );

  return {name: diagram.name, nodes, connections};
}

export type ClientDiagramNode = {
  id: DiagramNodeId;

  /** Friendly name given to the component. */
  label: string;
  /** Description. */
  description: string;
  /** ID for SVG icon. */
  icon: string;
  /** The position of the component in the diagram. */
  position: Vec2;
};

export type ClientDiagramConnection = {
  id: DiagramConnectionId;

  from: DiagramNodeId;
  to: DiagramNodeId;

  label: string;
  color: ColorPalette;
  anchors: ClientDiagramConnectionAnchor[];
};

export type ClientDiagramConnectionAnchor = {
  /** The position of the anchor. */
  position: Vec2;
};

export function convertNode(node: DiagramNode): ClientDiagramNode {
  return {
    id: node.diagram_node_id,

    label: node.label,
    description: node.description,
    icon: node.icon,

    position: Vec2.fromTuple(node.position),
  };
}

export function convertConnection(connection: DiagramConnection): ClientDiagramConnection {
  return {
    id: connection.diagram_connection_id,

    from: connection.source_node_id,
    to: connection.target_node_id,

    label: connection.label,
    color: typedIncludes(COLOR_PALETTES, connection.color) ? connection.color : "blue",
    anchors: connection.anchors.map(convertConnectionAnchor),
  };
}

export function convertConnectionAnchor(anchor: DiagramConnectionAnchor): ClientDiagramConnectionAnchor {
  return {position: Vec2.fromTuple(anchor.position)};
}

export function getAnyObject(id: DiagramAnyObjectId, diagram: ClientDiagram): DiagramAnyObject | null {
  switch (id.type) {
    case "Node": {
      const node = diagram.nodes.get(id.id);
      return node != null ? {type: "Node", node} : null;
    }
    case "Connection": {
      const connection = diagram.connections.get(id.id);
      return connection != null ? {type: "Connection", connection} : null;
    }
    case "ConnectionAnchor": {
      const connection = diagram.connections.get(id.id);

      if (connection == null) {
        return null;
      }

      const anchor = connection.anchors[id.index];

      if (anchor == null) {
        return null;
      }

      return {type: "ConnectionAnchor", connection, index: id.index, anchor};
    }
    case "Region":
      return null; // TODO(WF)
    default:
      unreachableCase(id);
  }
}

export type ClientDiagram = {
  name: string;

  nodes: Map<DiagramNodeId, ClientDiagramNode>;
  connections: Map<DiagramConnectionId, ClientDiagramConnection>;
};

export function isDiagramEmpty(diagram: ClientDiagram): boolean {
  return diagram.nodes.size === 0;
}

export type DiagramAction =
  | {action: "createNode"; node: ClientDiagramNode}
  | {action: "updateNode"; node: ClientDiagramNode}
  | {action: "deleteNode"; nodeId: DiagramNodeId}
  | {action: "createConnection"; connection: ClientDiagramConnection}
  | {action: "updateConnection"; connection: ClientDiagramConnection}
  | {action: "deleteConnection"; connectionId: DiagramConnectionId};

export function useDiagramEditor(diagramId: DiagramId, serverDiagram: Diagram) {
  const [diagram, setDiagram] = useState<ClientDiagram>(() => createClientDiagram(serverDiagram));
  const spatialIndex = useRef<SpatialIndex>(undefined! /* this is initialized in the useEffect */);

  const [toolState, setToolState] = useState<ToolState>(() => ({tool: "pan"}));

  const backend = useBackendSync(diagramId);

  // Update the client state whenever we receive new server state (incl. first time)
  useEffect(() => {
    // Avoid updating local state from the server while we have pending
    // local-only changes otherwise there will be "flickering". E.g:
    // (1) client has deleted node A locally, this has not been synced to the server yet
    // (2) receive update from server with node A still present, node A re-appears
    // (3) sync from (1) finishes, receive another update from server without node A, node A disappears again.
    if (backend.saving) {
      return;
    }

    const clientDiagram = createClientDiagram(serverDiagram);
    setDiagram(clientDiagram);

    spatialIndex.current = SpatialIndex.fromDiagram(clientDiagram);
    setToolState(prev => fixupToolState(prev, clientDiagram));
  }, [backend.saving, serverDiagram]);

  const dispatch = useCallback(
    (payload: DiagramAction) => {
      switch (payload.action) {
        case "createNode":
          {
            setDiagram(prev => {
              const nodes = new Map(prev.nodes);
              nodes.set(payload.node.id, payload.node);
              spatialIndex.current.putNode(payload.node);
              return {...prev, nodes};
            });

            backend.upsertNode(payload.node);
          }
          break;

        case "updateNode":
          {
            setDiagram(prev => {
              const nodes = new Map(prev.nodes);
              nodes.set(payload.node.id, payload.node);

              spatialIndex.current.putNode(payload.node);

              // Update associated connections
              for (const connection of prev.connections.values()) {
                if (connection.from === payload.node.id || connection.to === payload.node.id) {
                  dispatch({action: "updateConnection", connection});
                }
              }

              return {...prev, nodes};
            });

            backend.upsertNode(payload.node);
          }
          break;

        case "deleteNode":
          {
            setDiagram(prev => {
              const nodes = new Map(prev.nodes);
              const connections = new Map(prev.connections);

              nodes.delete(payload.nodeId);
              spatialIndex.current.remove({type: "Node", id: payload.nodeId});

              // Delete associated connections
              for (const connection of connections.values()) {
                if (connection.from === payload.nodeId || connection.to === payload.nodeId) {
                  dispatch({action: "deleteConnection", connectionId: connection.id});
                }
              }

              return {...prev, nodes, connections};
            });

            backend.deleteNode(payload.nodeId);
          }
          break;

        case "createConnection":
          {
            setDiagram(prev => {
              const connections = new Map(prev.connections);
              connections.set(payload.connection.id, payload.connection);
              spatialIndex.current.putConnection(payload.connection, prev.nodes, prev.connections);
              return {...prev, connections};
            });

            backend.upsertConnection(payload.connection);
          }
          break;

        case "updateConnection":
          {
            setDiagram(prev => {
              const connections = new Map(prev.connections);
              connections.set(payload.connection.id, payload.connection);
              spatialIndex.current.putConnection(payload.connection, prev.nodes, prev.connections);
              return {...prev, connections};
            });

            backend.upsertConnection(payload.connection);
          }
          break;

        case "deleteConnection":
          {
            setDiagram(prev => {
              const connections = new Map(prev.connections);
              connections.delete(payload.connectionId);
              spatialIndex.current.remove({type: "Connection", id: payload.connectionId});
              return {...prev, connections};
            });

            backend.deleteConnection(payload.connectionId);
          }
          break;

        default:
          unreachableCase(payload);
      }
    },
    [backend],
  );

  return useMemo(
    () => ({diagram: diagram, dispatch, spatialIndex, saving: backend.saving, toolState, setToolState}),
    [backend.saving, diagram, dispatch, toolState],
  );
}
